import { AxiosError } from 'axios';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import useGrecaptcha from '@/hooks/useGrecaptcha';
import { passwordApi } from '@/api';

interface Inputs {
  newPassword: string;
  newPasswordDoubleCheck: string;
}

interface UseChangePasswordFormProps {
  token: string;
}

const useChangePasswordForm = ({ token }: UseChangePasswordFormProps) => {
  const { t, i18n } = useTranslation();
  const executeGrecaptch = useGrecaptcha();
  const [isRequestSent, setIsRequestSent] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [validationRules, setValidationRules] = useState<string[]>([]);

  const language = useMemo(() => i18n.language, [i18n.language]);

  const schema = yup.object().shape({
    newPassword: yup
      .string()
      .required(t('changePasswordForm.validation.required')),
    newPasswordDoubleCheck: yup
      .string()
      .required(t('changePasswordForm.validation.required'))
      .oneOf([yup.ref('newPassword')], t('changePasswordForm.validation.passwordsMustMatch')),
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<Inputs>({
    criteriaMode: 'all',
    resolver: yupResolver(schema),
  });

  const fetchValidationRules = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await passwordApi.validationRules(language);
      setValidationRules([...response.data]);
    } finally {
      setIsLoading(false);
    }
  }, [language]);

  const serverPasswordValidation: SubmitHandler<Inputs> = async ({ newPassword, newPasswordDoubleCheck }) => {
    const recaptchaToken = await executeGrecaptch();
    await passwordApi.validatePassword({ newPassword, newPasswordDoubleCheck, recaptchaToken }, language);
  };

  const onSubmit: SubmitHandler<Inputs> = async ({ newPassword, newPasswordDoubleCheck }) => {
    try {
      await serverPasswordValidation({ newPassword, newPasswordDoubleCheck });
      const recaptchaToken = await executeGrecaptch();
      await passwordApi.changePassword({ newPassword, newPasswordDoubleCheck, recaptchaToken, resetToken: token }, language);
      setApiError(null);
      setIsRequestSent(true);
      reset();
    } catch (err) {
      const error = err as AxiosError<any>;
      if (error?.response?.data?.Reasons?.length > 0) {
        const reasons = error?.response?.data.Reasons as string[];

        setError('newPassword', {
          type: 'server',
          message: reasons[0],
          types: reasons.reduce<Record<string, string>>((acc, item, index) => (
            { ...acc, [`server${index}`]: item }
          ), {}),
        });
      } else {
        return t('changePasswordForm.generalError');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchValidationRules();
  }, [language, fetchValidationRules]);

  return {
    register,
    handleSubmit,
    onSubmit,
    errors,
    isRequestSent,
    isLoading,
    apiError,
    validationRules,
  };
};

export default useChangePasswordForm;
